/**
 * Phosphor Icons wrapper — typed `<Icon name="..." />` zamiast luźnych
 * `<i className="ph ph-folder" />` rozsianych po kodzie.
 *
 * **Implementacja:** named imports z `@phosphor-icons/react/ssr` — wariant
 * SSR-safe (bez `useContext`), renderowalny zarówno w Server jak i Client
 * Components. Tree-shaking: do bundla trafiają **tylko ikony z whitelisty
 * `IconName`** (~55 ikon ~ 30-40KB JS po gzip), zamiast całego webfontu
 * Phosphor regular (~147KB woff2 + 78KB CSS).
 *
 * **Migracja z `@phosphor-icons/web`:** poprzednio cały webfont był ładowany
 * przez `import "@phosphor-icons/web/regular"` w `app/layout.tsx`. Teraz każda
 * ikona to inline SVG generowany przez React — żaden font HTTP request, żaden
 * FOUC przy ładowaniu fontu, lepszy LCP na mobile.
 *
 * **Lista nazw (allowed values w `IconName`):** rozszerzamy wraz z potrzebami.
 * Strict whitelist gwarantuje że typo'wane "foldre" nie przejdzie przez
 * type-check — błąd w buildzie zamiast pustego SVG w UI. Dodanie nowej ikony:
 *   1. Dopisz literał do `IconName` union
 *   2. Dodaj wpis w `ICON_MAP` (kebab-case -> PascalCase z `@phosphor-icons/react/ssr`)
 *   3. Dopisz named import na górze pliku
 *
 * @see https://phosphoricons.com — pełna lista ikon (~9000+)
 * @see ../../.claude/CLAUDE.md sekcja "Ikony (Phosphor Icons)"
 */

import {
  House,
  Folder,
  SquaresFour,
  List,
  ListBullets,
  Plus,
  PencilSimple,
  Gear,
  Trash,
  ArrowCounterClockwise,
  ArrowSquareOut,
  X,
  Check,
  MagnifyingGlass,
  Lock,
  LockOpen,
  Eye,
  EyeSlash,
  Bell,
  User,
  UserCircle,
  CaretDown,
  CaretUp,
  CaretLeft,
  CaretRight,
  DotsThreeVertical,
  DotsThree,
  DotsSixVertical,
  ArrowsOutCardinal,
  Sun,
  Moon,
  CircleHalf,
  FlowerLotus,
  Warning,
  Info,
  CheckCircle,
  WarningCircle,
  XCircle,
  Image as ImageIcon,
  File as FileIcon,
  Files,
  FloppyDisk,
  Copy,
  Link as LinkIcon,
  Tag,
  Calendar,
  Translate,
  Phone,
  Envelope,
  Buildings,
  FacebookLogo,
  YoutubeLogo,
  LinkedinLogo,
  PinterestLogo,
  InstagramLogo,
  TwitterLogo,
  TiktokLogo,
  PersonArmsSpread,
  TextAa,
  ArrowClockwise,
  SpinnerGap,
  EnvelopeSimple,
  Tray,
  PaperPlaneTilt,
  PlusCircle,
  Archive,
  PuzzlePiece,
  Megaphone,
  Receipt,
  DownloadSimple,
  Signature,
  FileText,
  Printer,
  Cookie,
} from "@phosphor-icons/react/ssr";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";

export type IconName =
  //Navigation / panel structure
  | "house"
  | "folder"
  | "squares-four"  //layout grid
  | "list"
  | "list-bullets"
  //Actions
  | "plus"
  | "pencil-simple"
  | "gear"
  | "trash"
  | "arrow-counter-clockwise"  //undo / restore
  | "arrow-square-out"          //external link
  | "x"
  | "check"
  | "magnifying-glass"
  //State / status
  | "lock"
  | "lock-open"
  | "eye"
  | "eye-slash"
  | "bell"
  | "user"
  | "user-circle"
  | "caret-down"
  | "caret-up"
  | "caret-left"
  | "caret-right"
  | "dots-three-vertical"
  | "dots-three"
  //Drag handle / overlays
  | "dots-six-vertical"
  | "arrows-out-cardinal"
  //Theme switcher
  | "sun"
  | "moon"
  | "circle-half"   //high contrast
  | "flower-lotus"  //funeral / mourning mode
  //Notifications / messaging
  | "warning"
  | "info"
  | "check-circle"
  | "warning-circle"
  | "x-circle"
  //Files / content
  | "image"
  | "file"
  | "files"
  | "floppy-disk"   //save
  | "copy"
  | "link"
  | "tag"
  | "calendar"
  //i18n / locale
  | "translate"
  //Contact / address
  | "phone"
  | "envelope"
  | "buildings"
  //Social media (gated by `imperial_enabled_social_media` extension)
  | "facebook-logo"
  | "youtube-logo"
  | "linkedin-logo"
  | "pinterest-logo"
  | "instagram-logo"
  | "twitter-logo"
  | "tiktok-logo"
  //WCAG — panel dostępności cyfrowej (gated by `cms_wcag_availability` ext)
  | "person-arms-spread"  //trigger button next to ThemeToggle
  | "text-aa"             //font-size section header
  | "arrow-clockwise"     //reset to defaults
  | "spinner-gap"         //loading / autosave in-progress indicator
  //User account plugins (c_user_accounts extensions block renderers + sidebar icons)
  | "envelope-simple"     //pm_stat_unread, c_private_messages plugin
  | "tray"                //inbox link (received-messages)
  | "paper-plane-tilt"    //sent link (sent-messages)
  | "plus-circle"         //new message / add action
  | "archive"             //archive link
  | "receipt"             //c_facture plugin - faktury
  | "download-simple"     //pobranie PDF faktury
  | "signature"           //c_electronic_signs plugin - dokumenty do podpisu
  | "file-text"           //podgląd dokumentu
  | "printer"             //wydruk podstrony do PDF (CatalogAdminToolbar)
  | "puzzle-piece"        //fallback icon dla pluginów bez własnej ikony
  | "megaphone"           //c_advertisements plugin - lista ogłoszeń
  //RODO / cookie consent
  | "cookie";             //pływający przycisk ustawień zgód cookie

export type IconWeight = "thin" | "light" | "regular" | "bold" | "fill" | "duotone";

export type IconProps = {
  name: IconName;
  /** Default `regular`. Każda waga jest oddzielnym SVG path (per icon w bundle). */
  weight?: IconWeight;
  /** Decoracyjne ikony (default) — `aria-hidden`. Gdy `label` podany, ikona staje się focusowalna meta-info. */
  label?: string;
  className?: string;
  /** Wielkość SVG (default "1em" = wielkość rodzica przez `font-size`). */
  size?: number | string;
};

/* Mapping kebab-case (legacy IconName) -> komponent React z Phosphor.
   Każdy wpis ściąga jeden moduł `dist/ssr/{Name}.es.js` — tree-shaking
   gwarantuje że tylko wymienione ikony trafią do client bundle'a. */
const ICON_MAP: Record<IconName, PhosphorIcon> = {
  "house": House,
  "folder": Folder,
  "squares-four": SquaresFour,
  "list": List,
  "list-bullets": ListBullets,
  "plus": Plus,
  "pencil-simple": PencilSimple,
  "gear": Gear,
  "trash": Trash,
  "arrow-counter-clockwise": ArrowCounterClockwise,
  "arrow-square-out": ArrowSquareOut,
  "x": X,
  "check": Check,
  "magnifying-glass": MagnifyingGlass,
  "lock": Lock,
  "lock-open": LockOpen,
  "eye": Eye,
  "eye-slash": EyeSlash,
  "bell": Bell,
  "user": User,
  "user-circle": UserCircle,
  "caret-down": CaretDown,
  "caret-up": CaretUp,
  "caret-left": CaretLeft,
  "caret-right": CaretRight,
  "dots-three-vertical": DotsThreeVertical,
  "dots-three": DotsThree,
  "dots-six-vertical": DotsSixVertical,
  "arrows-out-cardinal": ArrowsOutCardinal,
  "sun": Sun,
  "moon": Moon,
  "circle-half": CircleHalf,
  "flower-lotus": FlowerLotus,
  "warning": Warning,
  "info": Info,
  "check-circle": CheckCircle,
  "warning-circle": WarningCircle,
  "x-circle": XCircle,
  "image": ImageIcon,
  "file": FileIcon,
  "files": Files,
  "floppy-disk": FloppyDisk,
  "copy": Copy,
  "link": LinkIcon,
  "tag": Tag,
  "calendar": Calendar,
  "translate": Translate,
  "phone": Phone,
  "envelope": Envelope,
  "buildings": Buildings,
  "facebook-logo": FacebookLogo,
  "youtube-logo": YoutubeLogo,
  "linkedin-logo": LinkedinLogo,
  "pinterest-logo": PinterestLogo,
  "instagram-logo": InstagramLogo,
  "twitter-logo": TwitterLogo,
  "tiktok-logo": TiktokLogo,
  "person-arms-spread": PersonArmsSpread,
  "text-aa": TextAa,
  "arrow-clockwise": ArrowClockwise,
  "spinner-gap": SpinnerGap,
  "envelope-simple": EnvelopeSimple,
  "tray": Tray,
  "paper-plane-tilt": PaperPlaneTilt,
  "plus-circle": PlusCircle,
  "archive": Archive,
  "receipt": Receipt,
  "download-simple": DownloadSimple,
  "signature": Signature,
  "file-text": FileText,
  "printer": Printer,
  "puzzle-piece": PuzzlePiece,
  "megaphone": Megaphone,
  "cookie": Cookie,
};

/**
 * Type guard — sprawdza czy podany string jest poprawną nazwą ikony z whitelist.
 * Używane do walidacji runtime (np. ikon zwracanych z REST API przez backend
 * pluginy `c_user_accounts`) — pozwala graceful fallback na nieznane nazwy
 * zamiast crash'a JSX (`ICON_MAP[unknown]` = undefined -> renderowanie wybucha).
 */
export function isIconName(name: unknown): name is IconName {
  return typeof name === "string" && name in ICON_MAP;
}

/**
 * Renderuje inline SVG (Phosphor SSR variant). Decoracyjne (no `label`) ->
 * `aria-hidden="true"`. Z `label` -> `role="img" aria-label={label}`.
 */
export function Icon({ name, weight = "regular", label, className, size }: IconProps) {
  const Component = ICON_MAP[name];
  if (label) {
    return (
      <Component
        weight={weight}
        size={size}
        className={className}
        role="img"
        aria-label={label}
      />
    );
  }
  return (
    <Component
      weight={weight}
      size={size}
      className={className}
      aria-hidden="true"
    />
  );
}
